import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject
} from '@angular/core';

import { FlexibleConnectedPositionStrategy, GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { MessageBus } from '@23degrees/shared/bus';
import { ResizeObserverDirective } from '@23degrees/shared/directives/resize-observer.directive';
import { AnalyticsService } from '@23degrees/shared/services/analytics';
import { UiStore } from '@23degrees/stores/ui/ui.store';
import { MenuService } from '@23degrees/ui/molecules/menu-button/menu.service';
import { DragDrop } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { DOCUMENT, NgClass, NgStyle } from '@angular/common';
import { EVENT_MESSAGES, MESSAGE_RECIPIENTS } from '@config';
import { Subject } from 'rxjs';
import { skip, take, takeUntil } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'menu-button',
  templateUrl: './menu-button.component.html',
  styleUrls: ['./menu-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, CdkScrollable, NgStyle, ResizeObserverDirective]
})
export class MenuButtonComponent {
  private document = inject(DOCUMENT);
  overlay = inject(Overlay);
  viewContainerRef = inject(ViewContainerRef);
  elem = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private messageBus = inject(MessageBus);
  private menuService = inject(MenuService);
  private analyticsService = inject(AnalyticsService);
  private dragDropService = inject(DragDrop);
  private uiStore = inject(UiStore);

  @HostBinding('class.disabled') @Input() disabled: boolean = false;

  @Input() shadowToBottom: boolean = false;
  @Input() hideShadow: boolean = false;
  @Input() id: string = uuid();
  @Input() connectToElement;

  @Input() stopPropagation: boolean = false;
  @Input() icon: string = 'plus';
  @Input() text: string;
  @Input() originPosition: any[] = ['end', 'bottom'];
  @Input() overlayPosition: any[] = ['end', 'top'];
  @Input() buttonClasses: string = 'button clean icon blank round no-shadow';
  @Input() buttonExtra: string = '';
  @Input() autoclose: boolean = false;
  @Input() withBackdrop: boolean = true;
  @Input() withBluring: boolean = false;
  @Input() relative: boolean = true;
  @Input() closeOnClick: boolean = false;
  @Input() openState: boolean = false;
  @Input() overlayStyle: any;
  @Input() menuStyle: any;
  @Input() logId: string;
  @Input() transparentBackdrop: boolean = true;
  @Input() showOverflow: boolean = false;
  @Input() fullwidth: boolean = false;
  @Input() flexibleDimensions: boolean = true;
  @Input() closeButton: boolean = false;
  @Input() draggable: boolean = false;
  @Input() preventDragStyle: boolean = false;
  @Input() scrollStrategy: 'close' | 'reposition' | 'block' = 'block';
  @Input() onTop: boolean = false;
  @Input() below: boolean = false;
  @Input() minSized: boolean = false;
  @Input() preventClosing: boolean = false;
  @Input() buttonCentered: boolean = true;
  @Input() notCloseOnButtonClick: boolean = false;
  @Input() withViewportMargin: boolean = true;
  @Input() withBorderRadius: boolean = false;

  @Output() isOpen = new EventEmitter();
  @Input()
  set close(val: boolean) {
    if (val !== this._closing) {
      this._closing = val;
      this.closeMenu();
    }
  }

  @ViewChild('template', { static: true }) template: TemplateRef<any>;
  @ViewChild('popup') popup: ElementRef;
  @ViewChild('buttonWrapper', { static: true }) buttonWrapper: ElementRef;
  @ViewChild('pointer') pointer: ElementRef;
  @ViewChild('handle') handle: ElementRef;

  get open() {
    return this.overlayRef?.hasAttached();
  }

  _closing: boolean = false;
  overlayRef: OverlayRef | null;

  outsideEnabled: boolean = false;

  pointerClasses: string[] = [];
  pointerOffsetVert: number = 0;
  pointerOffsetHor: number = 0;
  pointerStyle: any = {};

  positionStrategy: FlexibleConnectedPositionStrategy | GlobalPositionStrategy;

  closed$ = new Subject();

  ngOnInit(): void {
    if (this.menuService.menu) {
      if (!this.menuService.menu.hasAttached()) {
        this.openMenu(false);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.openState) {
      if (this.openState) {
        this.openMenu();
      } else {
        this.closeMenu();
      }
    }
  }

  ngOnDestroy(): void {
    this.closeMenu(false);
  }

  openMenu(autoOpen: boolean = false, event?: MouseEvent) {
    if (this.stopPropagation && event) {
      event.stopPropagation();
    }

    if (this.overlayRef && !autoOpen) {
      if (!this.notCloseOnButtonClick) {
        this.closeMenu();
      }
      return;
    }

    if (this.logId) {
      // this.analyticsService.sendEvent(this.logId, 'style', 'Style Element');
    }

    if (this.relative) {
      this.positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.connectToElement || this.elem)
        .withGrowAfterOpen(true)
        .withFlexibleDimensions(this.flexibleDimensions)
        .withPositions([
          {
            originX: this.originPosition[0],
            originY: this.originPosition[1],
            overlayX: this.overlayPosition[0],
            overlayY: this.overlayPosition[1],
          },
        ])
        .withPush(true)
        .withViewportMargin(this.withViewportMargin ? 10 : 0);

      // this.positionStrategy.positionChanges.pipe(takeUntil(this.closed$)).subscribe((position: ConnectedOverlayPositionChange) => {
      //     const { overlayX, overlayY } = position.connectionPair;
      //     this.pointerClasses = ['hor-' + overlayX, 'vert-' + overlayY];

      //     const { width } = this.buttonWrapper.nativeElement.getBoundingClientRect();
      //     const pointerBounds = this.pointer.nativeElement.getBoundingClientRect();

      //     if (overlayY === 'bottom' || overlayY === 'top') {
      //         if (overlayX === 'end') {
      //             this.pointerStyle = { right: width / 2 - pointerBounds.width / 2 + 'px' };
      //         }
      //         if (overlayX === 'start') {
      //             this.pointerStyle = { left: width / 2 - pointerBounds.width / 2 + 'px' };
      //         }
      //         if (overlayX === 'center') {
      //             this.pointerStyle = { left: 'calc(50% - ' + (pointerBounds.height / 2 + 'px') + ')' };
      //         }
      //     } else {
      //         this.pointerStyle = { top: 'calc(50% - ' + (pointerBounds.height / 2 + 'px') + ')' };
      //     }

      //     this.cdr.detectChanges();
      // });
    } else {
      this.positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
    }

    const panelClasses = [this.id];

    if ((this.transparentBackdrop || !this.withBackdrop || !this.shadowToBottom) && !this.hideShadow) panelClasses.push('with-shadow');
    if (this.shadowToBottom) panelClasses.push('with-shadow-to-bottom');
    if (this.withBorderRadius) panelClasses.push('with-border-radius');

    let scrollStrategy: any = this.overlay.scrollStrategies.block();
    if (this.scrollStrategy !== 'block') {
      scrollStrategy = this.scrollStrategy === 'reposition' ? this.overlay.scrollStrategies.reposition() : this.overlay.scrollStrategies.close();
    }

    this.overlayRef = this.overlay.create({
      backdropClass: this.transparentBackdrop ? 'backdrop-transparent' : '',
      hasBackdrop: this.withBackdrop,
      positionStrategy: this.positionStrategy,
      panelClass: panelClasses,
      minWidth: this.minSized ? this.buttonWrapper.nativeElement.getBoundingClientRect().width : null,
      scrollStrategy,
    });

    this.overlayRef.attach(new TemplatePortal(this.template, this.viewContainerRef));
    this.isOpen.emit(true);

    if (this.onTop) this.overlayRef.hostElement.classList.add('on-top');
    if (this.below) this.overlayRef.hostElement.classList.add('below');

    if (this.withBackdrop) {
      this.overlayRef
        .backdropClick()
        .pipe(take(1))
        .subscribe(() => {
          // console.log('CLOSE ON BACKDROP');
          this.closeMenu();
        });
    } else {
      // console.log('[MenuButtonComponent] - TODO implement else branch');
      // TODO: Get the clicked overlay to up front
      // this.overlayRef.hostElement.onclick = (ev) => {
      //   const overlay = (this.overlayRef.hostElement as HTMLElement);
      //   overlay.parentNode.appendChild(overlay);
      // };
    }

    if (!autoOpen) {
      this.menuService.setMenu(this.overlayRef, this.id);
    }

    if (this.withBluring) {
      this.uiStore.closeMenu(); // this.store.dispatch({ type: 'MENU_CLOSE' });
      this.uiStore.backgroundBlur(); // this.store.dispatch({ type: 'BACKGROUND_BLUR' });
    }

    if (this.autoclose && !this.withBackdrop) {
      this.messageBus
        .listen([EVENT_MESSAGES.Click], MESSAGE_RECIPIENTS.All)
        .pipe(skip(1), takeUntil(this.closed$))
        .subscribe((element) => {
          this.handleClick(element);
        });
    }

    if (this.draggable) {
      const drag = this.dragDropService.createDrag(this.overlayRef.hostElement);
      // drag.withBoundaryElement(this.overlayRef.hostElement.parentElement)

      // PREVENT DRAGGING ON INPUT ELEMENTS
      drag.getRootElement().onmousemove = (moveEvent: any) => {
        drag.disabled = moveEvent.target.nodeName === 'INPUT';
      };
      // ACTIVATE THESE TO JUST DRAG ON HANDLES
      // everything with class handle will work
      // const handles = [];
      // this.overlayRef.hostElement.querySelectorAll('.handle').forEach((element) => handles.push(element));
      // drag.withHandles(handles);
    }

    this.openState = false;
  }

  private handleClick = (targetElement) => {
    if (!targetElement || this.preventClosing) {
      return;
    }

    const clickedInside = this.popup?.nativeElement.contains(targetElement) || this.elem.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.closeMenu();
    }
  };

  public closeMenu(removeFromService: boolean = true) {
    if (this.overlayRef) {
      this.openState = false;
      this.overlayRef.dispose();
      this.overlayRef.detach();
      this.overlayRef = null;
      this.outsideEnabled = false;
      if (this.withBluring) {
        this.uiStore.backgroundClear();
      }
      if (removeFromService) {
        this.menuService.removeMenu(this.id);
      }
      this.isOpen.emit(false);
      this.closed$.next(null);
    }
  }

  public closeOnClicking() {
    if (this.closeOnClick) {
      this.closeMenu();
    }
  }

  public closeAction() {
    this.closeMenu();
  }

  public reposition() {
    if (this.overlayRef) {
      this.overlayRef.updatePosition();
    }
  }
}
